// frontend/src/pages/admin/AdminAlertsPage.js
//
// Admin alerts (super admin): members who did not reply to availability, did
// not check in / were late, or did not pay a fee by its due date. From one or
// several alerts the admin fines selected members (same amount for all, or
// each their own) or lets them go. Fines are published at once and one
// "Fines Issued" notice goes to every active member, with acknowledgement.
//
//   /admin/alerts                    list (open first) — tick several to review together
//   /admin/alerts/:id                one alert
//   /admin/alerts/batch?ids=a,b,c    several alerts on one screen

import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';

const NAVY = '#1E3A6E';
const KIND = {
  no_availability_reply: { icon: '✋', label: 'No reply' },
  checkin:               { icon: '📍', label: 'Check-in' },
  fee_overdue:           { icon: '💰', label: 'Fees' },
  fine_unpaid:           { icon: '🧾', label: 'Unpaid fines' },
  payment_recheck:       { icon: '🔁', label: 'Re-checks' },
};
const ISSUE = {
  no_reply:    { label: 'No reply',  bg: '#dbeafe', fg: '#1d4ed8' },
  no_show:     { label: 'No-show',   bg: '#fee2e2', fg: '#b91c1c' },
  late:        { label: 'Late',      bg: '#fef3c7', fg: '#92400e' },
  fee_overdue: { label: 'Unpaid',    bg: '#fee2e2', fg: '#b91c1c' },
  fee_late:    { label: 'Paid late', bg: '#ffedd5', fg: '#9a3412' },
  fine_unpaid: { label: 'Fine unpaid', bg: '#fee2e2', fg: '#b91c1c' },
  fine_late:   { label: 'Fine paid late', bg: '#ffedd5', fg: '#9a3412' },
  fee_upload_late: { label: 'Uploaded late', bg: '#e0e7ff', fg: '#3730a3' },
  recheck:     { label: 'Re-check', bg: '#e0e7ff', fg: '#3730a3' },
};
const fmtWhen = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const money = (v) => '₹' + Math.round(parseFloat(v) || 0).toLocaleString('en-IN');

function Header({ title, sub, back }) {
  return (
    <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '16px 16px 14px' }}>
      {back && <Link to={back} style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>← All alerts</Link>}
      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: back ? 6 : 0 }}>Admin · Alerts</div>
      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.35rem', color: NAVY }}>{title}</div>
      {sub && <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const chip = (on) => ({ padding: '6px 12px', borderRadius: 999, border: `1px solid ${on ? NAVY : '#e5e7eb'}`,
  background: on ? NAVY : 'white', color: on ? 'white' : '#374151', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' });

// ── Past alerts: preview, then create ─────────────────────────────────────
function PastAlerts({ onDone }) {
  const [state, setState] = useState('idle');     // idle | loading | preview | creating
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [err, setErr] = useState('');

  const preview = () => {
    setState('loading'); setErr('');
    api.get('/alerts/past')
      .then(r => { setData(r.data); setState('preview'); })
      .catch(e => { setErr(e.response?.data?.error || 'Failed'); setState('idle'); });
  };
  const create = () => {
    const nNew = data.alerts.filter(a => !a.exists).length;
    const nAdd = data.alerts.filter(a => a.exists).reduce((t, a) => t + a.new_members, 0);
    if (!window.confirm(`Create ${nNew} past alert${nNew === 1 ? '' : 's'}` +
        (nAdd ? ` and add ${nAdd} member${nAdd === 1 ? '' : 's'} to alerts already in the list` : '') +
        `?\n\nNo notice or push goes to anyone — they just appear in this list for you to decide.`)) return;
    setState('creating');
    api.post('/alerts/past')
      .then(r => { window.alert(`Created ${r.data.created} past alert${r.data.created === 1 ? '' : 's'}` +
          (r.data.added ? `, added ${r.data.added} member${r.data.added === 1 ? '' : 's'} to existing alerts` : '') + '.');
        setState('idle'); setData(null); onDone(); })
      .catch(e => { setErr(e.response?.data?.error || 'Failed'); setState('preview'); });
  };

  if (state === 'idle' || state === 'loading') {
    return (
      <div style={{ marginBottom: 14 }}>
        <button onClick={preview} disabled={state === 'loading'}
          style={{ width: '100%', padding: '10px', borderRadius: 10, border: `1.5px dashed ${NAVY}`, background: 'white',
            color: NAVY, fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'Rajdhani,sans-serif' }}>
          {state === 'loading' ? 'Working it out…' : '📜 Load past alerts (since the app started)'}
        </button>
        {err && <div style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: 6 }}>{err}</div>}
      </div>
    );
  }

  // Something to do: a new alert, or new members for an alert already in the list.
  const fresh = data.alerts.filter(a => a.new_members > 0);
  const count = (k) => fresh.filter(a => a.kind === k);
  const people = (list) => list.reduce((t, a) => t + a.members.length, 0);
  return (
    <div style={{ background: 'white', border: `1.5px solid ${NAVY}`, borderRadius: 12, padding: 14, marginBottom: 14 }}>
      <div style={{ fontWeight: 800, color: NAVY, fontFamily: 'Rajdhani,sans-serif', fontSize: '1.05rem' }}>Past alerts — preview</div>
      <div style={{ fontSize: '0.78rem', color: '#64748b', margin: '2px 0 10px' }}>
        Nothing is created yet. Members already fined, waived or excused for a miss are left out.
      </div>
      {['no_availability_reply', 'checkin', 'fee_overdue', 'fine_unpaid'].map(k => (
        <div key={k} style={{ fontSize: '0.85rem', marginBottom: 4 }}>
          {KIND[k].icon} <b>{KIND[k].label}</b>: {count(k).length} date{count(k).length === 1 ? '' : 's'}, {people(count(k))} member entr{people(count(k)) === 1 ? 'y' : 'ies'}
        </div>
      ))}
      <div style={{ maxHeight: 320, overflowY: 'auto', margin: '10px 0', borderTop: '1px solid #e5e7eb' }}>
        {data.alerts.map(a => (
          <div key={a.dedupe_key} style={{ borderBottom: '1px solid #f1f5f9', padding: '6px 0' }}>
            <div onClick={() => setOpen(o => ({ ...o, [a.dedupe_key]: !o[a.dedupe_key] }))}
              style={{ fontSize: '0.8rem', cursor: 'pointer', display: 'flex', gap: 6, opacity: a.new_members ? 1 : 0.5 }}>
              <span>{KIND[a.kind]?.icon}</span>
              <span style={{ flex: 1 }}>{a.title}</span>
              <span style={{ color: '#64748b' }}>
                {!a.exists ? `${a.members.length} ›` : a.new_members ? `+${a.new_members} to existing alert ›` : 'already in list'}
              </span>
            </div>
            {open[a.dedupe_key] && (
              <div style={{ fontSize: '0.74rem', color: '#475569', padding: '4px 0 2px 22px' }}>
                {a.members.map((m, i) => <div key={i}>{m.name} — {m.detail}</div>)}
              </div>
            )}
          </div>
        ))}
        {data.alerts.length === 0 && <div style={{ fontSize: '0.8rem', color: '#64748b', padding: 8 }}>Nothing found.</div>}
      </div>
      {err && <div style={{ color: '#dc2626', fontSize: '0.78rem', marginBottom: 6 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => { setState('idle'); setData(null); }}
          style={{ flex: 1, padding: 10, borderRadius: 10, border: '1px solid #e5e7eb', background: 'white', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
        <button onClick={create} disabled={!fresh.length || state === 'creating'}
          style={{ flex: 2, padding: 10, borderRadius: 10, border: 'none', background: fresh.length ? NAVY : '#94a3b8', color: 'white',
            fontWeight: 800, cursor: fresh.length ? 'pointer' : 'not-allowed', fontFamily: 'Rajdhani,sans-serif' }}>
          {state === 'creating' ? 'Creating…' : `Create / update ${fresh.length} alert${fresh.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}

// ── Fee alerts start month (migration_049) ─────────────────────────────────
function FeeStart() {
  const [v, setV] = useState('');
  const [saved, setSaved] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/alerts/settings').then(r => { setV(r.data.fee_alerts_from); setSaved(r.data.fee_alerts_from); }).catch(() => {});
  }, []);
  if (!saved) return null;
  async function save() {
    setBusy(true);
    try { const r = await api.post('/alerts/settings', { fee_alerts_from: v }); setSaved(r.data.fee_alerts_from); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  }
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', marginBottom: 12,
      display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: NAVY }}>💰 Fee alerts start from</span>
      <input type="month" className="form-control" value={v} onChange={e => setV(e.target.value)} style={{ width: 160, padding: '6px 8px' }} />
      {v !== saved && (
        <button onClick={save} disabled={busy}
          style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: NAVY, color: 'white', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      )}
      <span style={{ fontSize: '0.7rem', color: '#64748b', flexBasis: '100%' }}>
        Fees for earlier months are ignored by every fee alert (their payment records were reshuffled by the July rebalance).
      </span>
    </div>
  );
}

// ── List ──────────────────────────────────────────────────────────────────
function AlertList() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(null);
  const [kind, setKind] = useState('all');
  const [picked, setPicked] = useState(new Set());

  const load = useCallback(() => {
    api.get('/alerts').then(r => setAlerts(r.data.alerts || [])).catch(() => setAlerts([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (alerts === null) return <div className="loading-spinner"><div className="spinner" /></div>;
  const shown = alerts.filter(a => kind === 'all' || a.kind === kind);
  const open = shown.filter(a => a.status === 'open');
  const done = shown.filter(a => a.status === 'resolved');
  const allOn = open.length > 0 && open.every(a => picked.has(a.id));
  const pickedOpen = alerts.filter(a => picked.has(a.id) && a.status === 'open').map(a => a.id);
  async function deletePicked() {
    if (!window.confirm(`Delete ${picked.size} alert${picked.size === 1 ? '' : 's'}?\n\n` +
        `They disappear from this list and are never raised again. Fines already issued from them are NOT cancelled.`)) return;
    try {
      const r = await api.post('/alerts/delete-batch', { ids: [...picked] });
      window.alert(`Deleted ${r.data.deleted} alert${r.data.deleted === 1 ? '' : 's'}.`);
      setPicked(new Set()); load();
    } catch (e) { window.alert(e.response?.data?.error || 'Failed'); }
  }
  const togglePick = (id) => setPicked(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const card = (a) => {
    const k = KIND[a.kind] || { icon: '⚠️', label: a.kind };
    const isOpen = a.status === 'open';
    return (
      <div key={a.id} style={{ background: picked.has(a.id) ? '#f0f5ff' : 'white', border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${isOpen ? '#dc2626' : '#16a34a'}`, borderRadius: 10, padding: '10px 12px', marginBottom: 8,
        display: 'flex', gap: 10, alignItems: 'center' }}>
        <input type="checkbox" checked={picked.has(a.id)} onChange={() => togglePick(a.id)} style={{ width: 18, height: 18 }} />
        <Link to={`/admin/alerts/${a.id}`} style={{ textDecoration: 'none', flex: 1, minWidth: 0, display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: '1.3rem' }}>{k.icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: NAVY }}>{a.title}</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
              {a.context && a.context.history ? 'Past alert' : fmtWhen(a.created_at)} · {a.members} member{a.members === 1 ? '' : 's'}
              {isOpen ? ` · ${a.pending} to decide` : ' · done'}
            </div>
          </div>
          <span style={{ color: '#94a3b8' }}>›</span>
        </Link>
      </div>
    );
  };

  return (
    <div style={{ paddingBottom: 20 }}>
      <Header title="Alerts" sub="Missed availability replies, check-ins and fees. Tick several to decide or delete them together." />
      <div style={{ padding: 16 }}>
        <FeeStart />
        <PastAlerts onDone={load} />

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          <button style={chip(kind === 'all')} onClick={() => setKind('all')}>All</button>
          {Object.entries(KIND).map(([k, v]) => (
            <button key={k} style={chip(kind === k)} onClick={() => setKind(k)}>{v.icon} {v.label}</button>
          ))}
        </div>

        {picked.size > 0 && (
          <div style={{ position: 'sticky', top: 0, zIndex: 20, background: '#eef2ff', border: `1.5px solid ${NAVY}`,
            borderRadius: 12, padding: 10, marginBottom: 12, boxShadow: '0 4px 14px rgba(0,0,0,0.12)' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: NAVY, marginBottom: 8 }}>
              {picked.size} alert{picked.size === 1 ? '' : 's'} selected
              <button onClick={() => setPicked(new Set())}
                style={{ marginLeft: 8, border: 'none', background: 'none', color: '#64748b', fontSize: '0.72rem', textDecoration: 'underline', cursor: 'pointer' }}>clear</button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={deletePicked}
                style={{ flex: 1, padding: 12, borderRadius: 10, border: '1.5px solid #dc2626', background: 'white', color: '#dc2626',
                  fontWeight: 800, fontSize: '0.88rem', fontFamily: 'Rajdhani,sans-serif', cursor: 'pointer' }}>
                🗑 Delete ({picked.size})
              </button>
              {pickedOpen.length > 0 && (
                <button onClick={() => navigate(`/admin/alerts/batch?ids=${pickedOpen.join(',')}`)}
                  style={{ flex: 2, padding: 12, borderRadius: 10, border: 'none', background: NAVY, color: 'white',
                    fontWeight: 800, fontSize: '0.88rem', fontFamily: 'Rajdhani,sans-serif', cursor: 'pointer' }}>
                  Review {pickedOpen.length} open →
                </button>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ flex: 1, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', color: '#dc2626', textTransform: 'uppercase' }}>
            Open ({open.length})
          </div>
          {open.length > 0 && (
            <button onClick={() => setPicked(s => { const n = new Set(s); open.forEach(a => allOn ? n.delete(a.id) : n.add(a.id)); return n; })}
              style={{ ...chip(allOn), padding: '4px 10px' }}>{allOn ? '☑ All shown' : '☐ Select all shown'}</button>
          )}
        </div>
        {open.length === 0 && <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 16 }}>Nothing waiting for you. 🎉</div>}
        {open.map(card)}
        {done.length > 0 && (
          <>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', color: '#16a34a', textTransform: 'uppercase', margin: '18px 0 8px' }}>
              Done (last {done.length})
            </div>
            {done.map(card)}
          </>
        )}
      </div>
    </div>
  );
}

// ── Manual fines added on the review screen (SB, 29 Oct 2026) ─────────────
// Issued together with the alert fines when "Fine" is pressed, and listed in
// the same single "Fines Issued" notice to every member.
const CHARTER_PRESETS = [
  { group: 'Category 1 — ₹1,000', cat: 1, amount: 1000, items: [
    'Improper dress', 'Littering', 'Arguing with Management Board members regarding rules and guidelines',
    'Disobeying the captain in a match (on captain\u2019s complaint)', 'Fee / fine receipt not sent on UCC official number'] },
  { group: 'Category 2 — ₹3,000', cat: 2, amount: 3000, items: [
    'Repeat offender of Category 1 within the same quarter', 'Arguing with the umpires on and off the field',
    'Leaving the ground before completion of the match without permission', 'Abusing the umpire in person outside the ground',
    'Abusing / foul language / derogatory remarks against fellow members', 'Depositing monthly fee in cash in the UCC bank account',
    'Captain not following the batting / bowling quota',
    'Dissent to umpire\u2019s decision / minor argument on or off the field',
    'Adverse personal remark against a UCC player'] },
  { group: 'Category 3 — ₹6,000 + 2-match suspension', cat: 3, amount: 6000, items: [
    'Repeat offender of Category 2 within the same month', 'Smoking or drinking on the ground',
    'Intentional damage to a UCC asset', 'Abusing the umpire on the field', 'Match fixing or spot fixing',
    'Fighting / hitting a member, guest, umpire or support staff', 'Giving series availability and not turning up'] },
  // Charter notes under the table: penalty decided by the management, no set amount.
  { group: 'Major dissent — decided by the management (enter amount)', cat: 5, amount: null, items: [
    'Major dissent against the umpires or a fellow member during the game — 1-year captaincy ban (if captain) / 1-game series suspension (player)'] },
];
const SUSP = ' + 2-match suspension';

function ManualFines({ rows, setRows }) {
  const [members, setMembers] = useState(null);
  useEffect(() => {
    if (rows.length && members === null) {
      api.get('/members').then(r => setMembers((r.data.members || []).sort((a, b) => a.display_name.localeCompare(b.display_name))))
        .catch(() => setMembers([]));
    }
  }, [rows.length, members]);
  const upd = (k, patch) => setRows(rs => rs.map(r => r.key === k ? { ...r, ...patch } : r));
  const add = () => setRows(rs => [...rs, { key: Date.now() + Math.random(), member_id: '', preset: '', category: 5, amount: '', reason: '', pts: 0 }]);
  const pick = (k, value) => {
    if (!value) return upd(k, { preset: '' });
    if (value === 'other') return upd(k, { preset: 'other', category: 5, reason: '', amount: '' });
    const [gi, ii] = value.split(':').map(Number);
    const g = CHARTER_PRESETS[gi];
    upd(k, { preset: value, category: g.cat, amount: g.amount ? String(g.amount) : '', reason: g.items[ii] + (g.cat === 3 ? SUSP : '') });
  };
  return (
    <div style={{ background: 'white', border: '1px dashed #94a3b8', borderRadius: 10, padding: 12, marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1, fontWeight: 800, fontSize: '0.85rem', color: NAVY }}>➕ Manual fines {rows.length ? `(${rows.length})` : ''}</div>
        <button onClick={add} style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${NAVY}`, background: 'white', color: NAVY, fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>
          + Add a fine
        </button>
      </div>
      {!rows.length && <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 4 }}>Any other fines you want in the same "Fines Issued" notice.</div>}
      {rows.map(r => (
        <div key={r.key} style={{ borderTop: '1px solid #f1f5f9', marginTop: 10, paddingTop: 10, display: 'grid', gap: 6 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <select className="form-control" value={r.member_id} onChange={e => upd(r.key, { member_id: e.target.value })} style={{ flex: 1 }}>
              <option value="">{members === null ? 'Loading members…' : 'Choose member'}</option>
              {(members || []).map(m => <option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>)}
            </select>
            <button onClick={() => setRows(rs => rs.filter(x => x.key !== r.key))} title="Remove"
              style={{ border: '1px solid #fecaca', background: 'white', color: '#dc2626', borderRadius: 8, padding: '0 10px', cursor: 'pointer' }}>✕</button>
          </div>
          <select className="form-control" value={r.preset} onChange={e => pick(r.key, e.target.value)}>
            <option value="">Choose offence (charter)…</option>
            {CHARTER_PRESETS.map((g, gi) => (
              <optgroup key={gi} label={g.group}>
                {g.items.map((it, ii) => <option key={ii} value={`${gi}:${ii}`}>{it}</option>)}
              </optgroup>
            ))}
            <option value="other">Other — type the reason and amount</option>
          </select>
          <input className="form-control" placeholder="Reason (shown to the member and in the notice)" value={r.reason}
            onChange={e => upd(r.key, { reason: e.target.value })} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>₹</span>
            <input className="form-control" type="number" min="1" placeholder="Amount" value={r.amount}
              onChange={e => upd(r.key, { amount: e.target.value })} style={{ width: 110 }} />
            <span style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: 6 }}>Points</span>
            <input className="form-control" type="number" min="0" value={r.pts}
              onChange={e => upd(r.key, { pts: e.target.value })} style={{ width: 80 }} />
            {[1, 2, 3].includes(r.category) && <span style={{ fontSize: '0.7rem', fontWeight: 800, color: r.category === 3 ? '#6d28d9' : '#334155' }}>CAT {r.category}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Review: one alert or several ─────────────────────────────────────────
function AlertReview({ ids }) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);   // [{ alert, members }]
  const [err, setErr] = useState('');
  const [sel, setSel] = useState(new Set());    // `${alert_id}:${member_id}`
  const [amt, setAmt] = useState({});
  const [sameAmt, setSameAmt] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState([]);     // manual fines added on this screen
  const [autoApply, setAutoApply] = useState(null);  // Admin → Fines "auto-deduct points" switch
  useEffect(() => {
    api.get('/settings/fines-auto-apply').then(r => setAutoApply(r.data?.value === true)).catch(() => setAutoApply(null));
  }, []);
  const idKey = ids.join(',');

  const load = useCallback(() => {
    Promise.all(idKey.split(',').filter(Boolean).map(id => api.get('/alerts/' + id).then(r => r.data)))
      .then(list => {
        setGroups(list);
        const a = {}, s = new Set();
        list.forEach(g => g.members.forEach(m => {
          const key = `${g.alert.id}:${m.member_id}`;
          a[key] = m.suggested_amount != null ? String(Math.round(m.suggested_amount)) : '';
          if (m.decision === 'pending') s.add(key);
        }));
        setAmt(a); setSel(s);
      })
      .catch(e => setErr(e.response?.data?.error || 'Could not load the alert'));
  }, [idKey]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div><Header title="Alert" back="/admin/alerts" /><div style={{ padding: 16, color: '#dc2626' }}>{err}</div></div>;
  if (!groups) return <div className="loading-spinner"><div className="spinner" /></div>;

  const rows = groups.flatMap(g => g.members.map(m => ({ ...m, alert: g.alert, key: `${g.alert.id}:${m.member_id}` })));
  const pending = rows.filter(r => r.decision === 'pending');
  const chosen = pending.filter(r => sel.has(r.key));
  const allOn = pending.length > 0 && chosen.length === pending.length;
  const toggle = (key) => setSel(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleGroup = (g) => {
    const keys = g.members.filter(m => m.decision === 'pending').map(m => `${g.alert.id}:${m.member_id}`);
    const on = keys.every(k => sel.has(k));
    setSel(s => { const n = new Set(s); keys.forEach(k => on ? n.delete(k) : n.add(k)); return n; });
  };

  function applySame() {
    const v = parseFloat(sameAmt);
    if (!(v > 0)) return window.alert('Enter an amount first');
    setAmt(a => { const n = { ...a }; chosen.forEach(r => { n[r.key] = String(Math.round(v)); }); return n; });
  }

  async function submit(action) {
    if (!chosen.length && !(action === 'fine' && manual.length)) return window.alert('Tick at least one member');
    if (action === 'fine') {
      // A payment re-check row may be a correction only (₹0) — that is allowed.
      const bad = chosen.find(r => r.alert.kind !== 'payment_recheck' && !(parseFloat(amt[r.key]) > 0));
      if (bad) return window.alert(`Enter a fine amount for ${bad.display_name} (${bad.alert.title})`);
      const badM = manual.findIndex(m => !m.member_id || !(parseFloat(m.amount) > 0) || String(m.reason).trim().length < 3);
      if (badM >= 0) return window.alert(`Manual fine ${badM + 1}: choose the member and enter the reason and amount`);
      const total = chosen.reduce((t, r) => t + (parseFloat(amt[r.key]) || 0), 0) +
                    manual.reduce((t, m) => t + (parseFloat(m.amount) || 0), 0);
      const count = chosen.length + manual.length;
      if (!window.confirm(`Issue ${count} fine${count === 1 ? '' : 's'}` +
          `${manual.length ? ` (${chosen.length} from alerts + ${manual.length} manual)` : ''} — ${money(total)} in total?\n\n` +
          `Each fined member gets a notice, and every active member gets ONE "Fines Issued" notice listing all of them, to acknowledge.`)) return;
    } else if (!window.confirm(`Let ${chosen.length} go without a fine?` +
        (manual.length ? `\n\nYour ${manual.length} manual fine${manual.length === 1 ? ' is' : 's are'} only issued with "Fine", so not now.` : ''))) return;

    const byAlert = new Map();
    chosen.forEach(r => {
      if (!byAlert.has(r.alert.id)) byAlert.set(r.alert.id, []);
      byAlert.get(r.alert.id).push(action === 'fine'
        ? { member_id: r.member_id, action: 'fine', amount: parseFloat(amt[r.key]) }
        : { member_id: r.member_id, action: 'let_go' });
    });
    setBusy(true);
    try {
      const r = await api.post('/alerts/decide-batch', {
        groups: [...byAlert].map(([alert_id, items]) => ({ alert_id, items })),
        manual: action === 'fine'
          ? manual.map(m => ({ member_id: m.member_id, category: m.category, amount: parseFloat(m.amount),
                               reason: String(m.reason).trim(), pts: parseInt(m.pts) || 0 }))
          : undefined,
        note: note.trim() || undefined,
      });
      if (action === 'fine') setManual([]);
      const d = r.data;
      window.alert(
        (d.fined.length ? `Fined ${d.fined.length} — notices sent to them, and one "Fines Issued" notice to all members. ` : '') +
        (d.let_go ? `Let go ${d.let_go}. ` : '') +
        (d.resolved ? `${d.resolved} alert${d.resolved === 1 ? '' : 's'} now done.` : '')
      );
      const stillOpen = pending.length - chosen.length;
      if (stillOpen === 0) navigate('/admin/alerts'); else { setNote(''); load(); }
    } catch (e) {
      window.alert(e.response?.data?.error || 'Failed');
    } finally { setBusy(false); }
  }

  async function deleteThese() {
    const n = groups.length;
    if (!window.confirm(`Delete ${n === 1 ? 'this alert' : `these ${n} alerts`}?\n\n` +
        `${n === 1 ? 'It disappears' : 'They disappear'} from the list and ${n === 1 ? 'is' : 'are'} never raised again. ` +
        `Fines already issued are NOT cancelled; members not yet decided are simply dropped.`)) return;
    try {
      await api.post('/alerts/delete-batch', { ids: groups.map(g => g.alert.id) });
      navigate('/admin/alerts');
    } catch (e) { window.alert(e.response?.data?.error || 'Failed'); }
  }

  const btn = (bg, fg, border) => ({ flex: 1, padding: '12px', borderRadius: 10, border: border || 'none', background: bg, color: fg,
    fontWeight: 800, fontSize: '0.9rem', cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'Rajdhani,sans-serif', opacity: busy ? 0.6 : 1 });
  const single = groups.length === 1 ? groups[0].alert : null;
  const k1 = single ? (KIND[single.kind] || { icon: '⚠️', label: single.kind }) : null;

  return (
    <div style={{ paddingBottom: 90 }}>
      <Header back="/admin/alerts"
        title={single ? `${k1.icon} ${single.title}` : `${groups.length} alerts together`}
        sub={single
          ? `${k1.label} · raised ${fmtWhen(single.created_at)} · ${pending.length} of ${rows.length} still to decide`
          : `${pending.length} of ${rows.length} member entries still to decide`} />
      <div style={{ padding: 16 }}>
        {pending.length > 0 && (
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginBottom: 12,
            position: 'sticky', top: 0, zIndex: 5 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => setSel(allOn ? new Set() : new Set(pending.map(r => r.key)))}
                style={{ ...chip(allOn), borderRadius: 8 }}>
                {allOn ? '☑ All' : `☐ All (${pending.length})`}
              </button>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{chosen.length} selected</span>
              <span style={{ flex: 1 }} />
              <input type="number" min="1" placeholder="Same ₹ for selected" value={sameAmt}
                onChange={e => setSameAmt(e.target.value)} className="form-control"
                style={{ width: 170, fontSize: '0.8rem', padding: '6px 8px' }} />
              <button onClick={applySame}
                style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${NAVY}`, background: 'white', color: NAVY,
                  fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>Set</button>
            </div>
          </div>
        )}

        {groups.map(g => {
          const gp = g.members.filter(m => m.decision === 'pending');
          const k = KIND[g.alert.kind] || { icon: '⚠️' };
          return (
            <div key={g.alert.id} style={{ marginBottom: 14 }}>
              {!single && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 6px' }}>
                  <div style={{ flex: 1, fontWeight: 800, fontSize: '0.85rem', color: NAVY }}>{k.icon} {g.alert.title}</div>
                  {gp.length > 0 && (
                    <button onClick={() => toggleGroup(g)} style={{ ...chip(false), padding: '3px 8px', fontSize: '0.68rem' }}>
                      ☐/☑ this date
                    </button>
                  )}
                </div>
              )}
              {g.members.map(m => {
                const key = `${g.alert.id}:${m.member_id}`;
                const isPending = m.decision === 'pending';
                const iss = ISSUE[m.issue] || { label: m.issue || '', bg: '#f3f4f6', fg: '#374151' };
                return (
                  <div key={key}
                    style={{ background: isPending && sel.has(key) ? '#f0f5ff' : 'white', border: '1px solid #e5e7eb',
                      borderRadius: 10, padding: '10px 12px', marginBottom: 8, display: 'flex', gap: 10, alignItems: 'center',
                      opacity: isPending ? 1 : 0.7 }}>
                    {isPending
                      ? <input type="checkbox" checked={sel.has(key)} onChange={() => toggle(key)} style={{ width: 18, height: 18 }} />
                      : <span style={{ width: 18, textAlign: 'center' }}>{m.decision === 'fined' ? '⚠️' : '✓'}</span>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: NAVY }}>
                        {m.display_name} <span style={{ fontWeight: 400, fontSize: '0.7rem', color: '#64748b' }}>({m.ucc_id})</span>
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#475569', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {iss.label && <span style={{ background: iss.bg, color: iss.fg, borderRadius: 6, padding: '1px 6px', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase' }}>{iss.label}</span>}
                        {isPending && [1, 2, 3].includes(m.suggested_category) && (
                          <span style={{ background: m.suggested_category === 3 ? '#ede9fe' : '#f1f5f9', color: m.suggested_category === 3 ? '#6d28d9' : '#334155',
                            borderRadius: 6, padding: '1px 6px', fontWeight: 700, fontSize: '0.62rem' }}>CAT {m.suggested_category}</span>
                        )}
                        {isPending && m.suggested_pts > 0 && (
                          <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '1px 6px', fontWeight: 700, fontSize: '0.62rem' }}>−{m.suggested_pts} PTS</span>
                        )}
                        {m.detail}
                        {!isPending && (m.decision === 'fined'
                          ? <span style={{ color: '#b91c1c', fontWeight: 700 }}>· fined {money(m.fine_amount)}{m.fine_status === 'paid' ? ' (paid)' : ''}</span>
                          : <span style={{ color: '#16a34a', fontWeight: 700 }}>· let go</span>)}
                      </div>
                    </div>
                    {isPending && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>₹</span>
                        <input type="number" min="1" value={amt[key] ?? ''}
                          onChange={e => setAmt(a => ({ ...a, [key]: e.target.value }))}
                          className="form-control" style={{ width: 90, fontSize: '0.85rem', padding: '6px 8px' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {pending.length > 0 && (
          <>
            <ManualFines rows={manual} setRows={setManual} />
            <input className="form-control" placeholder="Note (optional) — e.g. had prior permission"
              value={note} onChange={e => setNote(e.target.value)} style={{ marginTop: 10 }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button disabled={busy || !chosen.length} onClick={() => submit('let_go')} style={btn('white', '#16a34a', '1.5px solid #16a34a')}>
                ✓ Let go ({chosen.length})
              </button>
              <button disabled={busy || (!chosen.length && !manual.length)} onClick={() => submit('fine')} style={btn('#dc2626', 'white')}>
                ⚠️ Fine ({chosen.length}{manual.length ? ` + ${manual.length} manual` : ''})
              </button>
            </div>
            {autoApply !== null && (
              <div style={{ fontSize: '0.75rem', marginTop: 8, borderRadius: 8, padding: '6px 10px',
                background: autoApply ? '#dcfce7' : '#fef3c7', color: autoApply ? '#166534' : '#92400e' }}>
                {autoApply
                  ? '✅ Penalty points are deducted from rankings as soon as you fine.'
                  : '⏸️ Penalty points will be PENDING — they are not deducted until you apply them in Admin → Fines.'}
                {' '}(Switch: Admin → Fines.)
              </div>
            )}
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 8 }}>
              Fines are published at once, to be deposited by the next Sunday (charter). Each fined member gets a notice, and every
              active member gets one "Fines Issued" notice listing all of them, which must be acknowledged.
            </div>
            {groups.some(g => g.alert.kind === 'payment_recheck') && (
              <div style={{ fontSize: '0.75rem', color: '#3730a3', background: '#eef2ff', borderRadius: 8, padding: '8px 10px', marginTop: 8 }}>
                🔁 Payment re-check: wrong late charges are reversed whichever button you press (money already paid on them
                goes to the member's fines credit, and he gets a "Fine Corrected" notice). <b>Fine</b> also issues the amount
                shown; <b>Let go</b> applies the correction only.
              </div>
            )}
          </>
        )}
        <button onClick={deleteThese}
          style={{ marginTop: 24, width: '100%', padding: 10, borderRadius: 10, border: '1px solid #fecaca', background: 'white',
            color: '#dc2626', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
          🗑 Delete {groups.length === 1 ? 'this alert' : `these ${groups.length} alerts`}
        </button>
      </div>
    </div>
  );
}

export default function AdminAlertsPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  if (id === 'batch') return <AlertReview ids={(params.get('ids') || '').split(',').filter(Boolean)} />;
  return id ? <AlertReview ids={[id]} /> : <AlertList />;
}
